import {
  ApiHotel,
  ApiHotelRoom,
  ApiPrice,
  ApiPriceFromJSON,
  ApiRoomContainer,
  ApiRoomContainerFromJSON
} from '@ibe/api';
import { ConfigModel } from '@ibe/services';

abstract class HotelUtils {
  static getThemedRooms(hotel: ApiHotel, themeCode: string): ApiHotelRoom[] {
    return this.sortRooms(hotel.rooms.filter(room => room.themeCode === themeCode));
  }

  static getThemedRoomsByRoomNumber(
    themedRooms: ApiHotelRoom[],
    roomNumber: number
  ): ApiHotelRoom[] {
    return themedRooms.filter(room => room.roomrates.some(rr => rr.roomNumber === roomNumber));
  }

  static groupRoomsByTheme(hotel: ApiHotel): ApiHotelRoom[] {
    return this.sortRooms(
      hotel.rooms.toSorted((r1, r2) => {
        return (r1.themeCode || '').localeCompare(r2.themeCode || '');
      })
    );
  }

  static sortRooms(rooms: ApiHotelRoom[]) {
    const isHighlighted = (room: ApiHotelRoom) => {
      const content = room?.metaInfo?.contentByMetaType?.ITEM_FRAME?.content;
      return content?.isFrameEnabled && !content?.isExcludedFromHighlightSorting;
    };

    const topSortedRooms = rooms.filter(isHighlighted);
    const normalRooms = rooms.filter(room => !isHighlighted(room));

    return [...topSortedRooms, ...this.sortRoomsByPrice(normalRooms)];
  }

  static sortRoomsByPrice(rooms: ApiHotelRoom[]) {
    return rooms.sort((a, b) => {
      const finalPriceA = this.findCheapestRoomPrice(a)?.finalPrice ?? null;
      const finalPriceB = this.findCheapestRoomPrice(b)?.finalPrice ?? null;

      if (finalPriceA === null && finalPriceB === null) {
        return 0;
      }
      if (finalPriceA === null || finalPriceB === null) {
        return finalPriceA === null ? 1 : -1;
      }

      return finalPriceA - finalPriceB;
    });
  }

  static findCheapestRoomPrice(room: ApiHotelRoom): ApiPrice | null {
    if (!room.roomrates || room.roomrates.length === 0) {
      return null;
    }

    let cheapestRoomPrice: ApiPrice;
    cheapestRoomPrice = room.roomrates[0].totalPrice;
    room.roomrates.forEach(roomRate => {
      const currentFinalPrice = roomRate.totalPrice?.finalPrice || 0;
      const cheapestRoomFinalPrice = cheapestRoomPrice?.finalPrice || 0;
      if (currentFinalPrice < cheapestRoomFinalPrice) {
        cheapestRoomPrice = roomRate.totalPrice;
      }
    });

    return cheapestRoomPrice;
  }

  static findRoomPriceByRoomNumber(room: ApiHotelRoom, roomNumber: number): ApiPrice | undefined {
    if (!room.roomrates || room.roomrates.length === 0) {
      return undefined;
    }

    return room.roomrates.find(roomRate => roomRate.roomNumber === roomNumber)?.totalPrice;
  }

  static getPersonsCount(roomContainer: ApiRoomContainer): number {
    let count = 0;

    count += roomContainer.adults;
    count += roomContainer.children;
    count += roomContainer.infants;

    return count;
  }

  static sumPrices(prices: ApiPrice[]): ApiPrice | undefined {
    if (prices.length < 1) {
      return ApiPriceFromJSON({
        finalPrice: 0,
        basePrice: 0,
        currencyCode: '',
        modifiers: [],
        isInclusive: false,
        isStartingAt: false
      });
    }
    const accumulator = prices[0];
    let sumFinal = 0;
    let sumBase = 0;
    for (const price of prices) {
      if (!price) {
        return undefined;
      }
      sumFinal += price.finalPrice;
      sumBase += price.basePrice;
    }
    return { ...accumulator, finalPrice: sumFinal, basePrice: sumBase };
  }

  /**
   * Should be the same as in HotelStore
   */
  static getRequestRoomContainer(occupancy: ApiRoomContainer[] | undefined, config?: ConfigModel) {
    let roomContainers = !!occupancy && occupancy.length > 0 ? [...occupancy] : undefined;
    if (config && config?.traveler?.adjustRoomContainerToConfig && roomContainers) {
      const { maxInfantAge, treatInfantsAsChildren } = config.traveler;
      roomContainers = roomContainers.map(roomContainer => {
        const infants = roomContainer.childrenAges
          ? roomContainer.childrenAges.filter((age: number) => age <= maxInfantAge).length
          : 0;
        const childrenAges = roomContainer.childrenAges
          ? roomContainer.childrenAges.filter((age: number) => age > maxInfantAge)
          : [];
        const infantsAges =
          roomContainer.childrenAges && !treatInfantsAsChildren
            ? roomContainer.childrenAges.filter((age: number) => age <= maxInfantAge)
            : [];
        const children = treatInfantsAsChildren
          ? roomContainer.childrenAges.filter((age: number) => age > maxInfantAge).length
          : childrenAges.length;
        const { adults } = roomContainer;

        return ApiRoomContainerFromJSON({ adults, children, infants, childrenAges, infantsAges });
      });
    }
    return roomContainers;
  }
}

export default HotelUtils;
